import { useEffect, useState } from 'react'

const SYNC_EVENT = 'splitsShowDustChanged'

type SyncDetail = { storageKey: string; value: boolean }

const readShowDust = (storageKey: string): boolean => {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(storageKey) === 'true'
}

// Small-transfers preference shared between a feed's toggle and its data hook,
// which both read the same key. react-use's useLocalStorage doesn't sync
// same-tab instances, so mirror useShowArchivedAccounts' CustomEvent sync.
export const useShowDust = (
  storageKey: string,
): [boolean, (value: boolean) => void] => {
  const [showDust, setShowDustState] = useState<boolean>(() =>
    readShowDust(storageKey),
  )

  const setShowDust = (value: boolean) => {
    setShowDustState(value)
    if (typeof window === 'undefined') return
    localStorage.setItem(storageKey, String(value))
    window.dispatchEvent(
      new CustomEvent<SyncDetail>(SYNC_EVENT, {
        detail: { storageKey, value },
      }),
    )
  }

  useEffect(() => {
    setShowDustState(readShowDust(storageKey))

    const handleSync = (event: Event) => {
      const detail = (event as CustomEvent<SyncDetail>).detail
      if (detail.storageKey === storageKey) {
        setShowDustState(detail.value)
      }
    }

    window.addEventListener(SYNC_EVENT, handleSync)
    return () => window.removeEventListener(SYNC_EVENT, handleSync)
  }, [storageKey])

  return [showDust, setShowDust]
}
